/**
 * CalculatorStateService - Servicio de estado local para la calculadora
 * 
 * Este servicio maneja el estado de la calculadora incluyendo:
 * - El tipo de trabajo seleccionado
 * - Los datos de cada step
 * - El step actual
 */

import { Injectable, signal, computed } from '@angular/core';
import { WorkTypeDto } from './work.service';

export interface CalculatorStepData {
  [key: string]: any;
}

export interface CalculatorFlow {
  id: string;
  name: string;
  description: string;
  totalSteps: number;
  stepSubTitles: string[];
  config: any;
}

@Injectable({ providedIn: 'root' })
export class CalculatorStateService {
  // Signals para el estado reactivo
  private _selectedWorkType = signal<WorkTypeDto | null>(null);
  private _currentStep = signal<number>(1);
  private _stepData = signal<Record<number, CalculatorStepData>>({});
  private _currentFlow = signal<CalculatorFlow | null>(null);
  private _forceShowBeachZoneSheet = signal<boolean>(false);

  // Configuraciones de flujos disponibles
  private readonly flowConfigs: Record<string, CalculatorFlow> = {
    'Losa y Viga': {
      id: 'losa-viga',
      name: 'Losa y Viga',
      description: 'Cálculo para estructura de losa y viga',
      totalSteps: 8,
      stepSubTitles: [
        '¿En qué región está tu obra?',
        'Ingresa las medidas',
        '¿Hay áreas libres o tragaluces?',
        'Selecciona el espesor del ladrillo a usar',
        '¿Hay vigas colgantes o peraltadas?',
        '¿Hay losa maciza?',
        '¿Hay escalera?',
        'Elige una resistencia'
      ],
      config: {
        useBricks: true,
        hasSkylights: true,
        hasBeams: true,
        hasSolidSlabs: true,
        hasStaircase: true
      }
    },
    'Cimentación': {
      id: 'cimentacion',
      name: 'Cimentación', 
      description: 'Cálculo para cimentación',
      totalSteps: 3,
      stepSubTitles: [
        '¿En qué región está tu obra?',
        'Ingresa las medidas',
        'Elige una resistencia',
      ],
      config: {
        useBricks: false,
        hasSoilType: true,
        hasDepth: true,
        hasReinforcement: true
      }
    },
    'Zapata': {
      id: 'zapata',
      name: 'Zapata', 
      description: 'Cálculo para zapata',
      totalSteps: 3,
      stepSubTitles: [
        '¿En qué región está tu obra?',
        'Ingresa las medidas',
        'Elige una resistencia',
      ],
      config: {
        useBricks: false,
        hasSoilType: true,
        hasDepth: true,
        hasReinforcement: true
      }
    },
    'Columna': {
      id: 'columna',
      name: 'Columna', 
      description: 'Cálculo para columna',
      totalSteps: 3,
      stepSubTitles: [
        '¿En qué región está tu obra?',
        'Ingresa las medidas',
        'Elige una resistencia',
      ],
      config: {
        useBricks: false,
        hasSoilType: true,
        hasDepth: true,
        hasReinforcement: true
      }
    },
    'Vereda': {
      id: 'vereda',
      name: 'Vereda', 
      description: 'Cálculo para vereda',
      totalSteps: 4,
      stepSubTitles: [
        '¿En qué región está tu obra?',
        'Ingresa las medidas',
        'Ingresa las medidas',
        'Elige una resistencia',
      ],
      config: {
        useBricks: false,
        hasSoilType: true,
        hasDepth: true,
        hasReinforcement: true
      }
    }
  };

  // Getters públicos (computed signals)
  readonly selectedWorkType = this._selectedWorkType.asReadonly();
  readonly currentStep = this._currentStep.asReadonly();
  readonly stepData = this._stepData.asReadonly();
  readonly currentFlow = this._currentFlow.asReadonly();
  readonly forceShowBeachZoneSheet = this._forceShowBeachZoneSheet.asReadonly();

  // Computed properties
  totalSteps = computed(() => 
    this._currentFlow()?.totalSteps ?? 0
  );

  stepSubTitles = computed(() => 
    this._currentFlow()?.stepSubTitles ?? []
  );

  currentStepSubTitle = computed(() => {
    const flow = this._currentFlow();
    const step = this._currentStep();
    return flow?.stepSubTitles[step - 1] ?? '';
  });

  isFirstStep = computed(() => 
    this._currentStep() === 1  // Step 1 is the first step of the specific flow
  );

  isLastStep = computed(() => 
    this._currentStep() === this.totalSteps()
  );

  canGoNext = computed(() => {
    const step = this._currentStep();
    const data = this._stepData();
    // TODO: Implementar validación específica por step
    return step < this.totalSteps();
  });

  /**
   * Establece el tipo de trabajo seleccionado y configura el flujo
   */
  setSelectedWorkType(workType: WorkTypeDto): void {
    this._selectedWorkType.set(workType);
    
    const flowConfig = this.flowConfigs[workType.description];
    if (flowConfig) {
      this._currentFlow.set(flowConfig);
    }
    
    // Reset step data and start at step 1 (region selection)
    this._currentStep.set(1);
    this._stepData.set({});
  }

  /**
   * Obtiene la configuración del flujo por descripción
   */
  getFlowConfig(description: string): CalculatorFlow | null {
    return this.flowConfigs[description] || null;
  }

  /**
   * Navega al siguiente step
   */
  nextStep(): boolean {
    const current = this._currentStep();
    const total = this.totalSteps();
    
    if (current < total) {
      this._currentStep.set(current + 1);
      return true;
    }
    
    return false;
  }

  /**
   * Navega al step anterior
   */
  previousStep(): boolean {
    const current = this._currentStep();
    
    // Can only go back to step 0 minimum
    if (current > 0) {
      this._currentStep.set(current - 1);
      return true;
    }
    
    return false;
  }

  /**
   * Va a un step específico
   */
  goToStep(step: number): boolean {
    const total = this.totalSteps();
    
    // Allow step 0 (region selection) to total steps
    if (step >= 0 && step <= total) {
      this._currentStep.set(step);
      return true;
    }
    
    return false;
  }

  /**
   * Guarda datos de un step específico
   */
  setStepData(step: number, data: CalculatorStepData): void {
    const currentData = this._stepData();
    this._stepData.set({
      ...currentData,
      [step]: { ...currentData[step], ...data }
    });
  }

  /**
   * Obtiene los datos de un step específico
   */
  getStepData(step: number): CalculatorStepData {
    return this._stepData()[step] || {};
  }

  /**
   * Obtiene todos los datos de todos los steps
   */
  getAllStepData(): Record<number, CalculatorStepData> {
    return this._stepData();
  }

  /**
   * Reinicia el estado de la calculadora
   */
  reset(): void {
    this._selectedWorkType.set(null);
    this._currentStep.set(1);  // Reset to step 1
    this._stepData.set({});
    this._currentFlow.set(null);
  }

  /**
   * Reinicia solo el flujo actual manteniendo el tipo de trabajo
   */
  resetFlow(): void {
    this._currentStep.set(1);  // Reset to step 1 (first step of flow)
    this._stepData.set({});
  }

  /**
   * Verifica si hay una sesión activa de calculadora
   */
  hasActiveSession(): boolean {
    return !!this._selectedWorkType();
  }

  /**
   * Marca que se debe forzar mostrar el beach zone bottom sheet
   */
  setForceShowBeachZoneSheet(force: boolean): void {
    this._forceShowBeachZoneSheet.set(force);
  }

  /**
   * Reinicia desde los resultados manteniendo datos pero forzando beach zone sheet
   */
  restartFromResults(): void {
    this._currentStep.set(1);
    this._forceShowBeachZoneSheet.set(true);
  }
}